import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const categories = ["All", "Quotations", "PCs", "Vehicles", "Chassis", "System"];
function targetFor(item) {
  switch (item.category) {
    case "Quotations": return "/quotes";
    case "PCs": return "/pcs";
    case "Vehicles": return "/admin";
    case "Chassis": return "/unresolved";
    default: return null;
  }
}
export default function NotificationHistoryPage({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [sort, setSort] = useState("newest");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reload = () => {
    setLoading(true); setError("");
    api.get("/notifications/history").then(r => setItems(r.data || []))
      .catch(e => setError(e.response?.data?.detail || "Could not load notification history"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);
  const visible = useMemo(() => items.filter(n => category === "All" || n.category === category)
    .sort((a, b) => sort === "newest"
      ? (b.created_at || "").localeCompare(a.created_at || "")
      : (a.created_at || "").localeCompare(b.created_at || "")), [items, category, sort]);
  const markRead = (item) => {
    if (item.read) return;
    api.post("/notifications/history/" + encodeURIComponent(item.id) + "/read")
      .then(() => { setItems(old => old.map(n => n.id === item.id ? { ...n, read: true } : n)); window.dispatchEvent(new Event("freshcare-notifications-changed")); })
      .catch(() => {});
  };
  return <div className={embedded ? "space-y-5" : "mx-auto max-w-4xl space-y-5 pb-20"} data-testid="notification-history-page">
    <div>
      {!embedded && <Link to="/" className="text-xs font-medium text-blue-700">← Home</Link>}
      <h1 className="mt-1 text-2xl font-semibold text-slate-900">Notification History</h1>
      <p className="mt-1 text-sm text-slate-500">Your latest 100 notifications. Admin sees organisation-wide activity.</p>
    </div>
    <div className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2">
      <label className="space-y-1 text-xs font-semibold text-slate-600">Order
        <select aria-label="Notification sort" value={sort} onChange={e => setSort(e.target.value)}
          className="block h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800">
          <option value="newest">Latest to oldest</option><option value="oldest">Oldest to latest</option>
        </select>
      </label>
      <label className="space-y-1 text-xs font-semibold text-slate-600">Category
        <select aria-label="Notification category" value={category} onChange={e => setCategory(e.target.value)}
          className="block h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800">
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
    </div>
    {loading && <div className="fc-card p-6 text-sm text-slate-500">Loading notifications…</div>}
    {!loading && error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error} <button onClick={reload} className="ml-2 underline">Retry</button></div>}
    {!loading && !error && <div className="space-y-2">
      <p className="text-xs text-slate-500">Showing {visible.length} of {items.length} recent notifications</p>
      {!visible.length && <div className="fc-card p-6 text-sm text-slate-500">No notifications in this category yet. New activity will appear here.</div>}
      {visible.map(n => <div key={n.id} className={"rounded-xl border p-4 " + (n.read ? "border-slate-200 bg-white" : "border-blue-200 bg-blue-50/70")}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-blue-700">{n.category}</span>
          {!n.read && <span className="text-[11px] font-semibold text-blue-700">Unread</span>}
          <span className="ml-auto text-xs text-slate-500">{n.created_at ? new Date(n.created_at).toLocaleString("en-IN") : ""}</span>
        </div>
        <div className="mt-2 text-sm font-semibold text-slate-900">{n.title}</div>
        <p className="mt-1 text-xs text-slate-600">{n.description}{n.actor_name ? " · " + n.actor_name : ""}</p>
        <div className="mt-3 flex flex-wrap gap-3">
          {!n.read && <button type="button" onClick={() => markRead(n)} className="text-xs font-semibold text-blue-700 underline">Mark as read</button>}
          {targetFor(n) && <Link onClick={() => markRead(n)} to={targetFor(n)} className="text-xs font-semibold text-blue-700 underline">Open category</Link>}
        </div>
      </div>)}
    </div>}
  </div>;
}

// freshcare-unread-dot-v1
